/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { Browser } from '@capacitor/browser';
import { Preferences } from '@capacitor/preferences';
import { useMutation, useQuery } from '@tanstack/react-query';
import allAppsIcon from 'assets/icons/ios/all.svg';
import entertainmentIcon from 'assets/icons/ios/entertainment.svg';
import gamesIcon from 'assets/icons/ios/games.svg';
import socialNetworkIcon from 'assets/icons/ios/social-networks.svg';
import screentimeImage from 'assets/screentime.jpg';
import AltusTalk from 'components/altus-talk/altus-talk';
import Button from 'components/button/button';
import Flex from 'components/flex/flex';
import Loader from 'components/loader/loader';
import OnboardingHeader from 'components/onboarding-header/onboarding-header';
import Page from 'components/page/page';
import RangeProgressBar from 'components/range-progress-bar/range-progress-bar';
import config from 'config';
import { useUserContext } from 'contexts/user-context';
import { useStatusBarHeight } from 'hooks/use-status-bar-height';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SegmentEventType, track } from 'segment';
import { getChildren } from 'services/childrens';
import { sendManagerAppProfile } from 'services/mdm';
import BlockerPlugin from 'utils/BlockerPlugin';

/* eslint-disable-next-line */
export interface BlockerProps {}

const StepLayout = ({
  canNext,
  onClick,
  children,
  text,
  background,
  secondaryButton,
}: {
  canNext: boolean;
  onClick: () => void;
  children: React.ReactNode;
  text?: string;
  background?: string;
  secondaryButton?: React.ReactNode;
}) => {
  const statusBarHeight = useStatusBarHeight();
  return (
    <Flex
      direction="column"
      justify="space-between"
      align="center"
      width="100%"
      height="100%"
      style={{
        paddingTop: 75 + statusBarHeight,
        paddingBottom: 20,
        overflowY: 'scroll',
        backgroundColor: background,
        position: 'fixed',
        top: 0,
      }}
    >
      <Flex direction="column" gap={20} pad={20} width="100%">
        {children}
      </Flex>
      <Flex direction="column" gap={10} pad={20} width="100%">
        <Button onClick={onClick} disabled={!canNext}>
          {text || 'SUIVANT'}
        </Button>
        {secondaryButton}
      </Flex>
    </Flex>
  );
};

export function BlockerIos(props: BlockerProps) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [step, setStep] = useState(0);
  const [mdmStepStarted, setMdmStepStarted] = useState(false);
  const { data: children } = useQuery({
    queryKey: ['children', id],
    queryFn: () => getChildren(id ?? ''),
    refetchInterval: mdmStepStarted ? 1000 : false,
  });

  const [screentimePermission, setScreentimePermission] = useState<boolean>();
  const [screentimeSuccessScreen, setScreentimeSuccessScreen] = useState<boolean>();
  const [mdmPermission, setMdmPermission] = useState<boolean>();
  const [mdmSuccessScreen, setMdmSuccessScreen] = useState<boolean>();

  const { parentalControlSettings } = useUserContext();
  const sendManagedAppProfileMutation = useMutation({
    mutationFn: sendManagerAppProfile,
    onSuccess: async () => {
      setStep((previous) => previous + 1);
    },
  });

  useEffect(() => {
    const checkPermissions = () => {
      BlockerPlugin.isScreenTimeEnabled().then(({ enabled }) => {
        setScreentimePermission(enabled);
        if (screentimeSuccessScreen === undefined) {
          setScreentimeSuccessScreen(enabled);
        }
      });
    };
    track(SegmentEventType.BLOCKER_CONFIGURATION_STARTED, {
      children_id: id,
    });
    checkPermissions();
    const interval = setInterval(checkPermissions, 1000);
    return () => clearInterval(interval);
  }, [screentimeSuccessScreen, id]);

  useEffect(() => {
    const hasChildrenMdm = !!children?.mdmUuid;
    setMdmPermission(hasChildrenMdm);
    if (mdmSuccessScreen === undefined) {
      setMdmSuccessScreen(hasChildrenMdm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  const setPhoneAsConfigured = async () => {
    if (parentalControlSettings) await BlockerPlugin.propagateParentalControlSettings(parentalControlSettings);
    await BlockerPlugin.turnOnBlocker();
    await Preferences.set({
      key: 'blockerConfigured',
      value: 'true',
    });
    track(SegmentEventType.BLOCKER_CONFIGURATION_COMPLETED, {
      children_id: id,
    });
    navigate('/dashboard');
  };

  const sendManagedAppProfile = async () => {
    await sendManagedAppProfileMutation.mutateAsync({ studentId: Number(id) });
  };

  const askPermission = async () => {
    await BlockerPlugin.askScreenTimePermission();
  };

  const selectEntertainmentApps = async () => {
    await BlockerPlugin.entertainmentAppsSelector().then(async () => {
      setStep((previous) => previous + 1);
    });
  };

  const selectAllApps = async () => {
    await BlockerPlugin.allAppsSelector().then(async () => {
      setStep((previous) => previous + 1);
    });
  };

  const askLocationPermission = async () => {
    await BlockerPlugin.askForLocationPermission().then(async () => {
      setStep((previous) => previous + 1);
    });
  };

  const downloadMdmProfile = async () => {
    setMdmStepStarted(true);
    await Browser.open({ url: `${config.api_baseurl}/mdm/enroll/${id}` });
  };

  if (!screentimePermission) {
    return (
      <Page>
        <Flex direction="column" justify="space-between" align="center" width="100%" height="100%">
          <OnboardingHeader percentage={0} onClick={() => navigate('/dashboard')} />
          <StepLayout canNext onClick={askPermission} text="J'AI COMPRIS">
            <AltusTalk>
              Cliquez sur continuer quand cet écran s&apos;affiche: <br />
              <img
                src={screentimeImage}
                onClick={askPermission}
                style={{ width: '100%', marginTop: 20, marginBottom: -5 }}
                alt="Screentime"
              />
            </AltusTalk>
          </StepLayout>
        </Flex>
      </Page>
    );
  }
  if (!screentimeSuccessScreen) {
    return (
      <Page>
        <StepLayout canNext onClick={() => setScreentimeSuccessScreen(true)} text="CONTINUER">
          <AltusTalk>Bravo ! J&apos;ai bien accès aux fonctionnalités de temps d&apos;écran</AltusTalk>
        </StepLayout>
      </Page>
    );
  }
  if (!mdmPermission) {
    return (
      <Page>
        <Flex direction="column" justify="space-between" align="center" width="100%" height="100%">
          <OnboardingHeader percentage={0} onClick={() => navigate('/dashboard')} />
          <StepLayout canNext onClick={downloadMdmProfile} text={mdmStepStarted ? 'RE-TÉLÉCHARGER' : 'SUIVANT'}>
            {mdmStepStarted ? (
              <AltusTalk>
                <Flex direction="column" align="center" gap={20}>
                  <Loader />
                  Je vérifie si le profil de configuration MDM est téléchargé !
                </Flex>
              </AltusTalk>
            ) : (
              <AltusTalk>Cliquez sur suivant pour télécharger le profil de configuration MDM</AltusTalk>
            )}
          </StepLayout>
        </Flex>
      </Page>
    );
  }
  if (!mdmSuccessScreen) {
    return (
      <Page>
        <StepLayout canNext onClick={() => setMdmSuccessScreen(true)} text="CONTINUER">
          <AltusTalk>Bravo ! J&apos;ai bien accès aux fonctionnalités de gestion de l&apos;appareil</AltusTalk>
        </StepLayout>
      </Page>
    );
  }
  const TOTAL_STEP = 5;
  return (
    <Page>
      <Flex direction="column" justify="space-between" align="center" width="100%" height="100%">
        <OnboardingHeader
          percentage={(step / TOTAL_STEP) * 100}
          onClick={() => (step > 0 ? setStep(step - 1) : navigate('/dashboard'))}
        />
        {step === 0 && (
          <StepLayout canNext onClick={sendManagedAppProfile} text="SUIVANT">
            <AltusTalk>Cliquez sur suivant pour autoriser l&apos;application à empécher sa désinstallation</AltusTalk>
          </StepLayout>
        )}
        {step === 1 && (
          <StepLayout canNext onClick={() => setStep((previous) => previous + 1)} text="SUIVANT">
            <AltusTalk>Cliquez sur &quot;gérer&quot; pour accepter la configuration</AltusTalk>
          </StepLayout>
        )}
        {step === 2 && (
          <StepLayout canNext onClick={selectEntertainmentApps} text="C'EST PARTI">
            <div
              style={{
                fontSize: 30,
                fontWeight: 800,
                textAlign: 'center',
                width: '100%',
                color: '#031D3D',
              }}
            >
              MODE JOUR
            </div>
            {children?.isLogoutRangeEnabled && (
              <RangeProgressBar
                hour={new Date().getHours() * 60 + new Date().getMinutes()}
                progress1={children?.logoutRangeMorning ?? 420}
                progress2={children?.logoutRangeEvening ?? 1140}
              />
            )}
            <div style={{ height: 20 }} />
            <AltusTalk variant="small" type="green_phone">
              <div style={{ marginBottom: 10 }}>
                Sélectionnez les <b>catégories</b> suivantes:
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                - <img src={entertainmentIcon} alt="Entertainment" width={20} /> <span>Divertissement</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                - <img src={socialNetworkIcon} alt="Social Networks" width={20} /> <span>Réseaux sociaux</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                - <img src={gamesIcon} alt="Games" width={20} /> <span>Jeux</span>
              </div>
              {/* <b>&quot;Divertissement&quot;</b>,{' '}
              <b>&quot;Réseaux sociaux&quot;</b>, et <b>&quot;Jeux&quot;</b>{' '}
              puis cliquez sur &quot;Sauvegarder&quot; */}
            </AltusTalk>
          </StepLayout>
        )}
        {step === 3 && (
          <StepLayout canNext onClick={selectAllApps} text="C'EST PARTI" background="#031D3D">
            <div
              style={{
                fontSize: 30,
                fontWeight: 800,
                textAlign: 'center',
                width: '100%',
                color: '#F0F7FF',
              }}
            >
              MODE NUIT
            </div>
            {children?.isLogoutRangeEnabled && (
              <RangeProgressBar
                hour={new Date().getHours() * 60 + new Date().getMinutes()}
                progress1={children?.logoutRangeEvening ?? 420}
                progress2={children?.logoutRangeMorning ?? 1140}
                darkMode
              />
            )}
            <div style={{ height: 20 }} />
            <AltusTalk variant="small" type="red_phone">
              <div style={{ marginBottom: 10 }}>
                Sélectionnez <b>toutes les applications</b> puis cliquez sur &quot;Sauvegarder&quot;
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                - <img src={allAppsIcon} alt="All Apps" width={20} /> <span>Toutes les applications</span>
              </div>
            </AltusTalk>
          </StepLayout>
        )}
        {step === 4 && (
          <StepLayout
            canNext
            onClick={askLocationPermission}
            secondaryButton={
              <Button
                outline
                onClick={() => {
                  setStep((previous) => previous + 1);
                }}
              >
                NE PAS ACTIVER
              </Button>
            }
            text="ACTIVER"
          >
            <AltusTalk>
              Souhaitez vous activer la <b>géolocalisation</b> pour le téléphone ?
              <br />
              <br />
              Vous serez en mesure de voir la <b>localisation</b> en direct de votre enfant
            </AltusTalk>
          </StepLayout>
        )}
        {step === 5 && (
          <StepLayout canNext onClick={setPhoneAsConfigured} text="ACCÉDER À MON ESPACE">
            <AltusTalk>Le téléphone est configuré !</AltusTalk>
          </StepLayout>
        )}
      </Flex>
    </Page>
  );
}

export default BlockerIos;
